import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ValidateEmail } from '../../../services/emailvalidations';
import { SendGridService } from '../../../services/sendgrid.service';
import {
  TextField, Button, colors, Box, Tooltip, IconButton, Dialog,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Paper,
  Autocomplete
} from '@mui/material'
import {getJwtToken} from '../../../services/jwt.services';
import { MaterialReactTable } from "material-react-table";
import { useCompanyId } from '../../../services/CompanyidProvider';
import LinearProgress from '@mui/material/LinearProgress';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {Delete as DeleteIcon, Close as CloseIcon,} from '@mui/icons-material';
import jwt_decode from 'jwt-decode';
import logger from '../../../utils/logger';
import styles from './bounces.module.css';
import { sendgridSearchHistoryUtils } from '../../../utils/sendgridSearchHistory';
// TODO Move out to common file/DB
const unblocktypes = [
  { name: 'Daily Digest', value: 'dailydigest', disabled: false },
  { name: 'Newsletters', value: 'newsletter', disabled: false },
  { name: 'Outreach', value: 'outreach', disabled: false },
  { name: 'MI Help', value: 'mi-help-prod', disabled: false },
  { name: 'Approved Senders', value: 'approved-sender-verification', disabled: false },
  { name: 'All', value: 'All', disabled: false },
];

export default function GetBounces() {
  const [email, setEmail] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const { companyId } = useCompanyId();
  const [reporttype, setReporttype] = useState(unblocktypes[0]); // Default to Daily Digest
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [permissions, setPermssiosn] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
 
  useEffect(() => {
    const jwtToken = getJwtToken();
   
    if (jwtToken) {
      const {sendgridpermissions} = jwt_decode(jwtToken);
      setPermssiosn(sendgridpermissions);
    }
    const history = sendgridSearchHistoryUtils.getHistory();
    setEmailHistory(history);
  }, []);

  
  const { isFetching, refetch } = useQuery({
    queryKey: [`sendgriddata.${email}`, companyId, reporttype.value],
    queryFn: () => SendGridService.getUserData(email, companyId, reporttype.value),
    enabled: false,
    onSuccess: (data) => {
      const responseData = data?.[0]?.data?.data;
      logger.debug('SendGrid response data:', responseData);
      setTableData(Array.isArray(responseData) ? responseData : []);
    }
  });
  
  const writepermssion = permissions.includes('write');

  const handleSubmit = () => {
    const validEmail = ValidateEmail(inputEmail);
    if (validEmail) {
      setEmail(inputEmail);
      sendgridSearchHistoryUtils.addToHistory(inputEmail);
      setEmailHistory(sendgridSearchHistoryUtils.getHistory());
      setTimeout(() => refetch(), 0);
    }
  };

  const handleReset = () => {
    setInputEmail('');
    setEmail('');
    setTableData([]);
    setError('');
    setReporttype(unblocktypes[0]); // Reset to Daily Digest
  };

  const handleItemClick = (item) => {
    setReporttype(item);
  };
  
  const handleEmailChange = (event, newValue) => {
    const value = newValue !== undefined ? newValue : event?.target?.value || '';
    setInputEmail(value);
    
    if (value && !ValidateEmail(value)) {
      setError('Please enter a valid email address.');
    } else {
      setError('');
    }
  };

  function getResultColor(resstring) {
    let rescolor = colors.green[500];
    switch (resstring?.toLowerCase()) {
      case 'unauthorized':
        rescolor = colors.yellow[500];
        break;
      case 'blocked':
        rescolor = colors.red[500];
        break;
      default:
    }
    return rescolor;
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'data.blocktype',
      header: 'Block Type',
      size: 150,
    },
    {
      accessorKey: 'data.email',
      header: 'Email',
      size: 150,
    },
    {
      accessorKey: 'data.status',
      header: 'Status',
      size: 150,
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={(theme) => ({
            backgroundColor:
              getResultColor(cell.getValue()),
            borderRadius: '0.25rem',
            color: 'black',
            minWidth: '20',
            p: '0.25rem',
          })}
        >
          {cell.getValue()?.toLocaleString?.('en-US')}
        </Box>
      ),
    },
    {
      accessorKey: 'data.reason',
      header: 'Reason',
      size: 150,
    }
  ], []);

  const EnableCheckStatus = ((companyId.length < 7 || !inputEmail || Boolean(error)));
  
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !EnableCheckStatus) {
      event.preventDefault();
      handleSubmit();
    }
  };
  
  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedId(null);
  };
  
  const handleConfirmDelete = async () => {
    if (selectedId !== null) {
      const {email, blocktype} = rows.original.data;
      const results = await SendGridService.UnblockUser(email, companyId, blocktype);
      if (results.status === 'ok') {
        setOpenDialog(false);
        setSelectedId(null);
      }
      refetch();
    }
  };
  
  const handleDeleteClick = (row) => {
    setSelectedId(row.index);
    setRows(row);
    setOpenDialog(true);
  };
  
  if (!permissions || !permissions.includes('read')) {
    return <div>You do not have permission to access this page.</div>;
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerRow}>
          <Button 
            className={styles.backButton}
            onClick={() => window.history.back()}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography variant="h6" className={styles.tableHeader}>
            Remove Bounce/Supression
          </Typography>
        </div>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.formRow}>
          <FormControl className={styles.selectField} required variant="outlined">
            <InputLabel id="block-type">Block Type</InputLabel>
            <Select
              labelId="block-type"
              id="block-type"
              value={reporttype}
              label="Block Type"
            >
              {unblocktypes.map((element, idx) => (
                <MenuItem 
                  key={idx} 
                  onClick={() => handleItemClick(element)} 
                  value={element}
                >
                  {element.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            className={styles.inputField}
            options={emailHistory}
            value={inputEmail}
            onInputChange={handleEmailChange}
            onChange={(event, newValue) => handleEmailChange(event, newValue)}
            renderInput={(params) => (
              <TextField 
                {...params}
                id="outlined-basic" 
                label="Email" 
                variant="outlined" 
                error={Boolean(error)}
                helperText={error}
                onKeyPress={handleKeyPress}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {emailHistory.length > 0 && (
                        <Tooltip title="Clear all history">
                          <IconButton
                            size="small"
                            onClick={() => setShowClearConfirm(true)}
                            edge="end"
                            sx={{ mr: 1 }}
                          >
                            <ClearAllIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px' }}>
                <span style={{ 
                  flex: 1, 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  marginRight: '8px'
                }}>
                  {option}
                </span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEmailToDelete(option);
                  }}
                  sx={{ ml: 1, flexShrink: 0 }}
                  title="Remove from history"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </li>
            )}
            ListboxProps={{
              style: { maxHeight: '250px' }
            }}
          />
          
          <Button 
            type="submit" 
            disabled={EnableCheckStatus} 
            className={styles.submitButton}
            variant="contained"
            onClick={handleSubmit}
          >
            Check Status
          </Button>
          
          <Button
            className={styles.resetButton}
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>
      </div>
      
      {isFetching && (
        <div className={styles.progressContainer}>
          <LinearProgress color="secondary" />
        </div>
      )}
      
      <Paper className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <MaterialReactTable
            enablePagination={false}
            columns={columns}
            data={tableData}
            enableSorting={false}
            enableBottomToolbar={false}
            enableRowActions={true}
            muiTableBodyRowProps={{ hover: false }}
            positionToolbarAlertBanner="bottom"
            enableColumnFilters={false}
            renderEmptyRowsFallback={() => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                padding: '2rem'
              }}>
                <Typography variant="body1" color="text.secondary">
                  {isFetching ? 'Loading...' : 'No records found. Enter an email and click "Check Status".'}
                </Typography>
              </Box>
            )}
            renderRowActions={({ row, table }) => (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <Tooltip arrow placement="right" title="Create Jira task">
                  <IconButton
                    color="primary"
                    onClick={() => window.open('https://meltwater.atlassian.net/servicedesk/customer/portal/43')}
                  >
                    <AddTaskIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip arrow placement="right" title="Remove Supression">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(row)}
                    disabled={row.original.data.status?.toLowerCase() !== 'blocked' || !writepermssion}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </div>
      </Paper>
      
      {/* Confirmation Dialog for Unblocking*/}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        PaperProps={{
          style: { borderRadius: '8px' }
        }}
      >
        <DialogTitle className={styles.dialogTitle}>Confirm Delete</DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText>
            {`Remove ${email} from the Bounce Supression list`}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={handleDialogClose} className={styles.dialogCancelButton}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} className={styles.dialogSubmitButton}>
            Delete Block
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All History Confirmation Dialog */}
      <Dialog
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        aria-labelledby="clear-history-dialog-title"
        aria-describedby="clear-history-dialog-description"
      >
        <DialogTitle id="clear-history-dialog-title">
          Clear Email Search History
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-history-dialog-description">
            Are you sure you want to clear all email search history? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              sendgridSearchHistoryUtils.clearHistory();
              setEmailHistory([]);
              setShowClearConfirm(false);
            }}
            color="error"
            variant="contained"
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Single Email Confirmation Dialog */}
      <Dialog
        open={Boolean(emailToDelete)}
        onClose={() => setEmailToDelete(null)}
        aria-labelledby="delete-email-dialog-title"
        aria-describedby="delete-email-dialog-description"
      >
        <DialogTitle id="delete-email-dialog-title">
          Remove Email from History
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-email-dialog-description">
            Are you sure you want to remove "{emailToDelete}" from your search history?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailToDelete(null)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (emailToDelete) {
                sendgridSearchHistoryUtils.removeFromHistory(emailToDelete);
                setEmailHistory(sendgridSearchHistoryUtils.getHistory());
                setEmailToDelete(null);
              }
            }}
            color="error"
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
